'use client'

import * as React from 'react'
import * as Sentry from '@sentry/nextjs'
import { ProductCard } from '@web/components/product-card/product-card'
import { marketplaceListProductsQueryFetcher } from '@web/gen/content'
import { MarketplaceLanguage, MarketplaceProductSortOrder } from '@web/gen/types'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import type { Locales } from 'cf-i18n/helpers'
import type { Language } from 'codegen/types'
import { useIntersectionObserver } from 'hooks/use-intersection-observer'

import type { GraphicsCategoryProduct } from './marketplace'
import { mapMarketplaceProductsToCategoryProducts } from './marketplace'

interface MarketplaceInfiniteProductLoaderProps {
  locale: Locales
  slug: string
  orderBy: string
  limit: number
  hidePriceSection?: boolean
  categoryUrlPrefix?: string
}

function mapLocaleToMarketplaceLanguage(locale: string): MarketplaceLanguage {
  switch (locale) {
    case 'de': {
      return MarketplaceLanguage.LanguageDe
    }
    case 'es': {
      return MarketplaceLanguage.LanguageEs
    }
    case 'fr': {
      return MarketplaceLanguage.LanguageFr
    }
    case 'it': {
      return MarketplaceLanguage.LanguageIt
    }
    case 'nl': {
      return MarketplaceLanguage.LanguageNl
    }
    case 'pl': {
      return MarketplaceLanguage.LanguagePl
    }
    case 'pt': {
      return MarketplaceLanguage.LanguagePt
    }
    default: {
      return MarketplaceLanguage.LanguageEn
    }
  }
}

function setSortingOptions(orderBy: string): MarketplaceProductSortOrder {
  switch (orderBy) {
    case 'date': {
      return MarketplaceProductSortOrder.ProductSortOrderMostRecent
    }
    default: {
      return MarketplaceProductSortOrder.ProductSortOrderMostPopular
    }
  }
}

function fetchMarketplaceProducts(params: {
  locale: string
  slug: string
  orderBy: string
  limit: number
  page: number
}) {
  const fetcher = marketplaceListProductsQueryFetcher({
    input: {
      categorySlug: params.slug,
      locale: mapLocaleToMarketplaceLanguage(params.locale),
      pagination: {
        currentPage: params.page,
        pageSize: params.limit,
      },
      sortType: setSortingOptions(params.orderBy),
    },
  })

  return fetcher()
}

export function MarketplaceInfiniteProductLoader({
  locale,
  slug,
  orderBy,
  limit,
  hidePriceSection = false,
  categoryUrlPrefix,
}: MarketplaceInfiniteProductLoaderProps) {
  const [products, setProducts] = React.useState<GraphicsCategoryProduct[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasMore, setHasMore] = React.useState(true)
  const [isInitialized, setIsInitialized] = React.useState(false)
  const hasTriggeredInThisIntersection = React.useRef(false)
  const pageRef = React.useRef(1)

  const [ref, entry] = useIntersectionObserver<HTMLDivElement>()
  const isIntersecting = entry?.isIntersecting ?? false

  const loadPage = React.useCallback(
    async (pageNum: number) => {
      const data = await fetchMarketplaceProducts({
        locale,
        slug,
        orderBy,
        limit,
        page: pageNum,
      })

      return {
        products: mapMarketplaceProductsToCategoryProducts(
          data?.marketplaceListProducts?.products,
          locale,
          categoryUrlPrefix,
        ),
        totalCount: data?.marketplaceListProducts?.pagination?.totalCount || 0,
      }
    },
    [categoryUrlPrefix, limit, locale, orderBy, slug],
  )

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const pageParam = urlParams.get('page')

    if (pageParam !== null) {
      const urlPage = Number.parseInt(pageParam, 10)

      if (Number.isNaN(urlPage) || urlPage <= 1) {
        const url = new URL(window.location.href)
        url.searchParams.delete('page')
        window.history.replaceState({}, '', url.toString())
        setIsInitialized(true)
        return
      }

      pageRef.current = urlPage
      setIsLoading(true)

      loadPage(urlPage)
        .then(data => {
          if (data.products.length) {
            setProducts(data.products)
          }

          if (urlPage * limit >= data.totalCount) {
            setHasMore(false)
          }

          setIsLoading(false)
          setIsInitialized(true)
        })
        .catch(error => {
          Sentry.captureException(error)
          setIsLoading(false)
          setIsInitialized(true)
        })

      return
    }

    setIsInitialized(true)
  }, [limit, loadPage])

  const loadMore = React.useCallback(async () => {
    const nextPage = pageRef.current + 1
    setIsLoading(true)
    pageRef.current = nextPage

    const url = new URL(window.location.href)
    url.searchParams.set('page', String(nextPage))
    window.history.pushState({}, '', url.toString())

    try {
      const data = await loadPage(nextPage)

      if (data.products.length) {
        setProducts(prev => [...prev, ...data.products])
      }

      if (nextPage * limit >= data.totalCount) {
        setHasMore(false)
      }
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      setIsLoading(false)
    }
  }, [limit, loadPage])

  React.useEffect(() => {
    if (!isIntersecting) {
      hasTriggeredInThisIntersection.current = false
    }
  }, [isIntersecting])

  React.useEffect(() => {
    if (
      isInitialized &&
      isIntersecting &&
      !hasTriggeredInThisIntersection.current &&
      hasMore &&
      !isLoading
    ) {
      hasTriggeredInThisIntersection.current = true
      void loadMore()
    }
  }, [hasMore, isInitialized, isIntersecting, isLoading, loadMore])

  if (products.length === 0 && !isLoading) {
    return <div ref={ref} className="h-px w-full bg-transparent" />
  }

  return (
    <>
      {products.map(product => (
        <ProductCard
          key={product.productId}
          productId={String(product.productId || 0)}
          productUrl={addOrReplaceLocaleInUrl(product.productUrl || '', locale as Language)}
          imageClassName="aspect-[3/2] object-cover"
          image={product.productImage || ''}
          name={product.productTitleLocalized?.[locale] || product.productTitle || ''}
          alt={product.imageAlt || ''}
          category={product.cardCategory?.[locale] || ''}
          categoryUrl={
            product.categoryUrl
              ? addOrReplaceLocaleInUrl(product.categoryUrl || '', locale as Language)
              : undefined
          }
          countdown={
            product.productCountdown
              ? new Date(product.productCountdown.replace(/\//g, '-')).getTime()
              : undefined
          }
          hidePriceSection={hidePriceSection}
          isLegacy
        />
      ))}
      <div ref={ref} className="h-px w-full bg-transparent" />
    </>
  )
}
